// ============================================================
// CommissionDrawer.jsx
// Commission overview — earnings, deal table, CEM profile editor
// Accessible: Director (full), Senior Viewer (read-only), BD (own only)
// ============================================================
import { useState, useEffect, useCallback } from 'react';
import { X, Edit2, Check, AlertCircle, Download } from 'lucide-react';
import { api } from '../api';
import { useUser } from '../context/UserContext';

const fmt = (v) => v == null ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (v) => v == null ? '—' : `${(parseFloat(v) * 100).toFixed(2)}%`;
const PERIODS = [
  { label: '1M',  value: 'month' },
  { label: '3M',  value: 'quarter' },
  { label: '6M',  value: 'half' },
  { label: '12M', value: 'year' },
  { label: 'All', value: 'all' },
];
const BANDS = [
  { label: '≤£100k',       key: 'band1' },
  { label: '£100k–£500k',  key: 'band2' },
  { label: '£500k–£1m',    key: 'band3' },
  { label: '>£1m',         key: 'band4' },
];

export default function CommissionDrawer({ onClose }) {
  const { user } = useUser();
  const isDirector    = user?.role === 'director';
  const isSeniorViewer = user?.role === 'senior_viewer';
  const isBD          = user?.role === 'bd';

  // ── State ──────────────────────────────────────────────────
  const [period, setPeriod]         = useState('year');
  const [cemList, setCemList]       = useState([]);   // all CEMs (director only)
  const [selectedCem, setSelectedCem] = useState(null); // userId being viewed
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  // Profile editor
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm]       = useState(null);
  const [profileSaving, setProfileSaving]   = useState(false);
  const [profileError, setProfileError]     = useState('');
  const [profileSaved, setProfileSaved]     = useState(false);

  // Deal table sort
  const [sortField, setSortField] = useState('won_date');
  const [sortDir, setSortDir]     = useState('desc');
  const [exporting, setExporting] = useState(false);

  // ── Load CEM list (director sees all) ─────────────────────
  useEffect(() => {
    if (!isDirector && !isSeniorViewer) {
      setSelectedCem(user?.id);
      return;
    }
    const load = async () => {
      try {
        const data = await api.get('/api/commission/users-for-cem');
        // Filter out placeholder/test accounts
        const real = data.filter(u =>
          u.first_name && u.last_name &&
          !u.last_name.startsWith('(') &&
          u.first_name.toLowerCase() !== (u.role || '').toLowerCase()
        );
        setCemList(real);
        if (real.length > 0) setSelectedCem(real[0].id);
      } catch (e) {
        setError('Failed to load CEM list');
      }
    };
    load();
  }, [isDirector, isSeniorViewer, user?.id]);

  // ── Load summary when CEM or period changes ───────────────
  useEffect(() => {
    if (!selectedCem) return;
    const load = async () => {
      setLoading(true);
      setError('');
      setSummary(null);
      try {
        const data = await api.get(`/api/commission/summary/${selectedCem}?period=${period}`);
        setSummary(data);
      } catch (e) {
        // 404 = no CEM profile yet — show empty state with create option
        if (e.message && e.message.includes('not found')) {
          setSummary({ profile: null, deals: [], lead_fee_total: 0, current_retainer_monthly: 0 });
        } else {
          setError(e.message || 'Failed to load commission data');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedCem, period]);

  // ── Profile editor helpers ────────────────────────────────
  const openProfileEditor = () => {
    // Use existing profile or system defaults for new profile
    const p = summary?.profile || {};
    setProfileForm({
      cem_type:              p.cem_type              || 'principal',
      engagement_start_date: p.engagement_start_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      is_active:             p.is_active             ?? true,
      gp_threshold:          toDisplayPct(p.gp_threshold)      || '18',
      rate_self_band1:       toDisplayPct(p.rate_self_band1)    || '2.5',
      rate_self_band2:       toDisplayPct(p.rate_self_band2)    || '2',
      rate_self_band3:       toDisplayPct(p.rate_self_band3)    || '1.5',
      rate_self_band4:       toDisplayPct(p.rate_self_band4)    || '1',
      rate_lead_band1:       toDisplayPct(p.rate_lead_band1)    || '1.5',
      rate_lead_band2:       toDisplayPct(p.rate_lead_band2)    || '1.25',
      rate_lead_band3:       toDisplayPct(p.rate_lead_band3)    || '1',
      rate_lead_band4:       toDisplayPct(p.rate_lead_band4)    || '0.65',
      rate_intro_band1:      toDisplayPct(p.rate_intro_band1)   || '1',
      rate_intro_band2:      toDisplayPct(p.rate_intro_band2)   || '0.75',
      rate_intro_band3:      toDisplayPct(p.rate_intro_band3)   || '0.5',
      rate_intro_band4:      toDisplayPct(p.rate_intro_band4)   || '0.35',
      lead_fee_pence:        p.lead_fee_pence != null ? (p.lead_fee_pence / 100).toFixed(2) : '30.00',
      repeat_m1_6_pct:       toDisplayPct(p.repeat_m1_6_pct)   || '50',
      repeat_m7_12_pct:      toDisplayPct(p.repeat_m7_12_pct)  || '25',
      repeat_m13_18_rate:    toDisplayPct(p.repeat_m13_18_rate) || '0.25',
      stage1_pct:            toDisplayPct(p.stage1_pct)         || '70',
      stage2_pct:            toDisplayPct(p.stage2_pct)         || '30',
      notes:                 p.notes || '',
    });
    setEditingProfile(true);
    setProfileError('');
    setProfileSaved(false);
  };

  const toDisplayPct = (v) => v != null ? (parseFloat(v) * 100).toFixed(4).replace(/\.?0+$/, '') : '';
  const fromDisplayPct = (v) => v !== '' ? (parseFloat(v) / 100).toFixed(6) : null;

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileError('');
    try {
      
      const payload = {
        ...profileForm,
        gp_threshold:       fromDisplayPct(profileForm.gp_threshold),
        rate_self_band1:    fromDisplayPct(profileForm.rate_self_band1),
        rate_self_band2:    fromDisplayPct(profileForm.rate_self_band2),
        rate_self_band3:    fromDisplayPct(profileForm.rate_self_band3),
        rate_self_band4:    fromDisplayPct(profileForm.rate_self_band4),
        rate_lead_band1:    fromDisplayPct(profileForm.rate_lead_band1),
        rate_lead_band2:    fromDisplayPct(profileForm.rate_lead_band2),
        rate_lead_band3:    fromDisplayPct(profileForm.rate_lead_band3),
        rate_lead_band4:    fromDisplayPct(profileForm.rate_lead_band4),
        rate_intro_band1:   fromDisplayPct(profileForm.rate_intro_band1),
        rate_intro_band2:   fromDisplayPct(profileForm.rate_intro_band2),
        rate_intro_band3:   fromDisplayPct(profileForm.rate_intro_band3),
        rate_intro_band4:   fromDisplayPct(profileForm.rate_intro_band4),
        lead_fee_pence:     profileForm.lead_fee_pence !== '' ? Math.round(parseFloat(profileForm.lead_fee_pence) * 100) : null,
        repeat_m1_6_pct:    fromDisplayPct(profileForm.repeat_m1_6_pct),
        repeat_m7_12_pct:   fromDisplayPct(profileForm.repeat_m7_12_pct),
        repeat_m13_18_rate: fromDisplayPct(profileForm.repeat_m13_18_rate),
        stage1_pct:         fromDisplayPct(profileForm.stage1_pct),
        stage2_pct:         fromDisplayPct(profileForm.stage2_pct),
        user_id:            selectedCem,
      };
      await api.post('/api/commission/cem-profiles', payload);
      setProfileSaved(true);
      setEditingProfile(false);
      // Reload summary to reflect new rates
      const data = await api.get(`/api/commission/summary/${selectedCem}?period=${period}`);
      setSummary(data);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (e) {
      setProfileError(e.message || 'Save failed');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Excel export ──────────────────────────────────────────
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('ipe_token');
      const res = await fetch(`/api/commission/export-excel?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `IPE_Commission_${period}_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── Sort helpers ──────────────────────────────────────────
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const sortedDeals = summary?.deals ? [...summary.deals].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  }) : [];

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color: 'var(--text-dim)', marginLeft: 3 }}>⇅</span>;
    return <span style={{ color: 'var(--teal)', marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const totals = summary?.deals ? {
    submitted: summary.deals.reduce((s, d) => s + (d.submitted_value || 0), 0),
    nc:        summary.deals.reduce((s, d) => s + (d.nc_total || 0), 0),
    tgpv:      summary.deals.reduce((s, d) => s + (d.tgpv || 0), 0),
    gross:     summary.deals.reduce((s, d) => s + (d.commission_gross || 0), 0),
    stage1:    summary.deals.reduce((s, d) => s + (d.stage1 || 0), 0),
    stage2:    summary.deals.reduce((s, d) => s + (d.stage2 || 0), 0),
  } : null;

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 1100,
      }} />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(1100px, 95vw)',
        background: 'var(--surface)',
        borderLeft: '1px solid var(--border)',
        zIndex: 1101,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
      }}>

        {/* ── Drawer header ── */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Commission</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {isDirector ? 'All CEMs — Director view' : isSeniorViewer ? 'All CEMs — Read only' : 'Your earnings & deal history'}
            </div>
          </div>

          {/* CEM selector (Director only) */}
          {(isDirector || isSeniorViewer) && cemList.length > 0 && (
            <select
              value={selectedCem || ''}
              onChange={e => setSelectedCem(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--surface)', color: 'var(--text)', fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {cemList.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}{c.cem_type ? ` — ${c.cem_type}` : ' — No profile'}
                </option>
              ))}
            </select>
          )}

          {/* Period selector */}
          <div style={{ display: 'flex', gap: 2, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: 3 }}>
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)} style={{
                padding: '4px 12px', borderRadius: 4, border: 'none',
                background: period === p.value ? 'var(--teal)' : 'transparent',
                color: period === p.value ? '#fff' : 'var(--text-muted)',
                fontWeight: period === p.value ? 600 : 400,
                fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
              }}>{p.label}</button>
            ))}
          </div>


          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={21} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 60, fontSize: 13 }}>
              Loading commission data…
            </div>
          )}

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {summary && !loading && (<>

            {/* ── KPI strip ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total Commission', value: fmt(totals?.gross), highlight: true },
                { label: 'Stage 1 (70%)',    value: fmt(totals?.stage1) },
                { label: 'Stage 2 (30%)',    value: fmt(totals?.stage2) },
                { label: 'Lead Fees',        value: fmt(summary.lead_fee_total) },
                { label: 'Retainer (curr.)', value: fmt(summary.current_retainer_monthly) + '/mo' },
              ].map(k => (
                <div key={k.label} style={{
                  background: k.highlight ? 'var(--teal-dim)' : 'var(--surface2)',
                  border: `1px solid ${k.highlight ? 'var(--teal-glow)' : 'var(--border)'}`,
                  borderRadius: 8, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'DM Mono, monospace', color: k.highlight ? 'var(--teal)' : 'var(--text)' }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* ── Deal table ── */}
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                Deal History
                <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>({sortedDeals.length} deals)</span>
                {(isDirector || isSeniorViewer) && (
                  <button onClick={handleExportExcel} disabled={exporting} style={{
                    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', background: 'var(--surface2)',
                    border: '1px solid var(--border)', borderRadius: 5,
                    fontSize: 12, fontWeight: 500, cursor: exporting ? 'wait' : 'pointer',
                    color: 'var(--teal)', fontFamily: 'inherit', opacity: exporting ? 0.6 : 1,
                  }}>
                    <Download size={13} /> {exporting ? 'Exporting...' : 'Export Excel'}
                  </button>
                )}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                      {[
                        { label: 'EST Ref',       field: 'est_reference' },
                        { label: 'Project',       field: 'project_name' },
                        { label: 'Client',        field: 'company_name' },
                        { label: 'Won',           field: 'won_date' },
                        { label: 'Submitted',     field: 'submitted_value' },
                        { label: 'NC Deductions', field: 'nc_total' },
                        { label: 'TGPV',          field: 'tgpv' },
                        { label: 'Rate',          field: 'rate' },
                        { label: 'Stage 1',       field: 'stage1' },
                        { label: 'Stage 2',       field: 'stage2' },
                        { label: 'Status',        field: 'status' },
                      ].map(col => (
                        <th key={col.field}
                          onClick={() => toggleSort(col.field)}
                          style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
                          {col.label}<SortIcon field={col.field} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeals.length === 0 && (
                      <tr><td colSpan={11} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>No deals in this period</td></tr>
                    )}
                    {sortedDeals.map((d, i) => (
                      <tr key={d.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 1 ? 'var(--surface2)' : 'transparent' }}>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--teal)', whiteSpace: 'nowrap' }}>{d.est_reference || '—'}</td>
                        <td style={{ padding: '7px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.project_name}>{d.project_name}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{d.company_name || '—'}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{d.won_date ? new Date(d.won_date).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'2-digit' }) : '—'}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{fmt(d.submitted_value)}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: d.nc_total > 0 ? '#dc2626' : 'var(--text-dim)' }}>{d.nc_total > 0 ? `−${fmt(d.nc_total)}` : '—'}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', fontWeight: 600 }}>{fmt(d.tgpv)}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{fmtPct(d.rate)}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: 'var(--teal)' }}>{fmt(d.stage1)}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: 'var(--text-muted)' }}>{fmt(d.stage2)}</td>
                        <td style={{ padding: '7px 10px' }}>
                          <span style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                            background: d.status === 'won' ? 'rgba(5,150,105,0.1)' : d.status === 'submitted' ? 'rgba(14,154,170,0.1)' : 'rgba(217,119,6,0.1)',
                            color: d.status === 'won' ? '#047857' : d.status === 'submitted' ? 'var(--teal)' : '#b45309',
                          }}>{d.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {totals && sortedDeals.length > 0 && (
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface)', fontWeight: 700 }}>
                        <td colSpan={4} style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>Totals</td>
                        <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{fmt(totals.submitted)}</td>
                        <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#dc2626' }}>{totals.nc > 0 ? `−${fmt(totals.nc)}` : '—'}</td>
                        <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{fmt(totals.tgpv)}</td>
                        <td style={{ padding: '8px 10px' }}></td>
                        <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: 'var(--teal)' }}>{fmt(totals.stage1)}</td>
                        <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace', textAlign: 'right', color: 'var(--text-muted)' }}>{fmt(totals.stage2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── CEM Profile (Director only) ── */}
            {isDirector && (
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>CEM Profile — Rate Card</span>
                  {profileSaved && <span style={{ fontSize: 11, color: 'var(--teal)' }}>✓ Saved</span>}
                  {!editingProfile ? (
                    <button onClick={openProfileEditor} style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                      background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 5,
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}><Edit2 size={14} /> {summary.profile ? 'Edit Profile' : 'Create Profile'}</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setEditingProfile(false)} style={{ padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 5, background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={handleProfileSave} disabled={profileSaving} style={{ padding: '5px 12px', background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {profileSaving ? 'Saving…' : <><Check size={14} style={{ marginRight: 4 }} />Save</>}
                      </button>
                    </div>
                  )}
                </div>

                {profileError && (
                  <div style={{ margin: '10px 14px', padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>
                    {profileError}
                  </div>
                )}

                <div style={{ padding: '16px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

                  {/* Left: Meta */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 10 }}>Profile</div>
                    {[
                      { label: 'Type',            field: 'cem_type',              type: 'select', options: ['principal','introducer'] },
                      { label: 'Start Date',       field: 'engagement_start_date', type: 'date' },
                      { label: 'GP Threshold (%)', field: 'gp_threshold',          type: 'pct' },
                      { label: 'Stage 1 Split (%)', field: 'stage1_pct',           type: 'pct' },
                      { label: 'Stage 2 Split (%)', field: 'stage2_pct',           type: 'pct' },
                      { label: 'Lead Fee (£)',      field: 'lead_fee_pence',        type: 'text' },
                      { label: 'Active',            field: 'is_active',             type: 'checkbox' },
                    ].map(f => renderField(f, profileForm, setProfileForm, editingProfile, summary.profile))}

                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 10, marginTop: 18 }}>Repeat Business Taper</div>
                    {[
                      { label: 'Months 1–6 (%)',    field: 'repeat_m1_6_pct',    type: 'pct' },
                      { label: 'Months 7–12 (%)',   field: 'repeat_m7_12_pct',   type: 'pct' },
                      { label: 'Months 13–18 rate', field: 'repeat_m13_18_rate', type: 'pct' },
                    ].map(f => renderField(f, profileForm, setProfileForm, editingProfile, summary.profile))}
                  </div>

                  {/* Right: Rate bands */}
                  <div>
                    {[
                      { label: 'Self-Sourced Rates', prefix: 'rate_self' },
                      { label: 'Lead-Provided Rates', prefix: 'rate_lead' },
                      { label: 'Introducer Bonus Rates', prefix: 'rate_intro' },
                    ].map(group => (
                      <div key={group.prefix} style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 8 }}>{group.label}</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', padding: '3px 6px', textAlign: 'left' }}>Band</th>
                              <th style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', padding: '3px 6px', textAlign: 'right' }}>Rate %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {BANDS.map(band => {
                              const fieldKey = `${group.prefix}_${band.key}`;
                              const displayVal = editingProfile
                                ? (profileForm?.[fieldKey] ?? '')
                                : fmtPct(summary.profile?.[fieldKey]);
                              return (
                                <tr key={band.key} style={{ borderBottom: '1px solid var(--border)' }}>
                                  <td style={{ padding: '5px 6px', fontSize: 11, color: 'var(--text-muted)' }}>{band.label}</td>
                                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                                    {editingProfile ? (
                                      <input
                                        type="number" step="0.01" min="0" max="100"
                                        value={profileForm?.[fieldKey] ?? ''}
                                        onChange={e => setProfileForm(f => ({ ...f, [fieldKey]: e.target.value }))}
                                        style={{ width: 70, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)', fontSize: 11, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}
                                      />
                                    ) : (
                                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{displayVal}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}

                    {/* Retainer Schedule */}
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 8, marginTop: 16 }}>Retainer Schedule</div>
                    {(() => {
                      const schedule = (() => {
                        try {
                          const raw = editingProfile ? profileForm?.retainer_schedule : summary.profile?.retainer_schedule;
                          return Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
                        } catch { return []; }
                      })();
                      if (!schedule.length) return (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No retainer schedule defined</div>
                      );
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', padding: '3px 6px', textAlign: 'left' }}>Period</th>
                              <th style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', padding: '3px 6px', textAlign: 'right' }}>Monthly (£)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {schedule.map((slot, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '5px 6px', fontSize: 11, color: 'var(--text-muted)' }}>
                                  Month {slot.from_month}–{slot.to_month}
                                </td>
                                <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                                  {editingProfile ? (
                                    <input
                                      type="number" min="0" step="1"
                                      value={(slot.amount_pence / 100).toFixed(0)}
                                      onChange={e => {
                                        const updated = schedule.map((s, j) =>
                                          j === i ? { ...s, amount_pence: Math.round(parseFloat(e.target.value || 0) * 100) } : s
                                        );
                                        setProfileForm(f => ({ ...f, retainer_schedule: updated }));
                                      }}
                                      style={{ width: 80, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)', fontSize: 11, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}
                                    />
                                  ) : (
                                    <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11 }}>
                                      {fmt(slot.amount_pence / 100)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}

                    {/* Notes */}
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 6, marginTop: 16 }}>Notes</div>
                    {editingProfile ? (
                      <textarea
                        value={profileForm?.notes || ''}
                        onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))}
                        rows={3}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: summary.profile?.notes ? 'normal' : 'italic' }}>
                        {summary.profile?.notes || 'No notes'}
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

          </>)}
        </div>
      </div>
    </>
  );
}

// ── Field renderer helper ─────────────────────────────────────
function renderField(f, form, setForm, editing, profile) {
  const rawVal = profile?.[f.field];
  const displayVal = (() => {
    if (f.type === 'pct') return fmtPctStatic(rawVal);
    if (f.type === 'checkbox') return rawVal ? 'Yes' : 'No';
    if (f.type === 'date') return rawVal ? rawVal.slice(0, 10) : '—';
    return rawVal ?? '—';
  })();

  return (
    <div key={f.field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', gap: 8 }}>
      <label style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{f.label}</label>
      {editing && form ? (
        f.type === 'select' ? (
          <select value={form[f.field] || ''} onChange={e => setForm(v => ({ ...v, [f.field]: e.target.value }))}
            style={{ padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit' }}>
            {f.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : f.type === 'checkbox' ? (
          <input type="checkbox" checked={!!form[f.field]} onChange={e => setForm(v => ({ ...v, [f.field]: e.target.checked }))} />
        ) : (
          <input type={f.type === 'date' ? 'date' : 'text'}
            value={form[f.field] ?? ''}
            onChange={e => setForm(v => ({ ...v, [f.field]: e.target.value }))}
            style={{ width: 120, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontFamily: 'DM Mono, monospace', textAlign: 'right' }}
          />
        )
      ) : (
        <span style={{ fontSize: 12, fontFamily: f.type === 'pct' || f.type === 'text' ? 'DM Mono, monospace' : 'inherit', color: 'var(--text)' }}>{displayVal}</span>
      )}
    </div>
  );
}

function fmtPctStatic(v) {
  return v != null ? `${(parseFloat(v) * 100).toFixed(2)}%` : '—';
}
